import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiService } from '../../shared/services';
import * as BillingActions from './billing.actions';

@Injectable()
export class BillingEffects {

  loadTimeEntries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadTimeEntries),
      switchMap(({ matterId, userId, isBilled, dateFrom, dateTo, skip, limit }) =>
        this.apiService.getTimeEntries({ matterId, userId, isBilled, dateFrom, dateTo, skip, limit }).pipe(
          map(response => BillingActions.loadTimeEntriesSuccess({ response })),
          catchError(err =>
            of(BillingActions.loadTimeEntriesFailure({ error: err?.error?.message ?? 'Failed to load time entries' }))
          )
        )
      )
    )
  );

  createTimeEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.createTimeEntry),
      switchMap(({ payload }) =>
        this.apiService.createTimeEntry(payload).pipe(
          map(entry => BillingActions.createTimeEntrySuccess({ entry })),
          catchError(err =>
            of(BillingActions.createTimeEntryFailure({ error: err?.error?.message ?? 'Failed to create time entry' }))
          )
        )
      )
    )
  );

  updateTimeEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.updateTimeEntry),
      switchMap(({ id, payload }) =>
        this.apiService.updateTimeEntry(id, payload).pipe(
          map(entry => BillingActions.updateTimeEntrySuccess({ entry })),
          catchError(err =>
            of(BillingActions.updateTimeEntryFailure({ error: err?.error?.message ?? 'Failed to update time entry' }))
          )
        )
      )
    )
  );

  deleteTimeEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.deleteTimeEntry),
      switchMap(({ id }) =>
        this.apiService.deleteTimeEntry(id).pipe(
          map(() => BillingActions.deleteTimeEntrySuccess({ id })),
          catchError(err =>
            of(BillingActions.deleteTimeEntryFailure({ error: err?.error?.message ?? 'Failed to delete time entry' }))
          )
        )
      )
    )
  );

  loadExpenses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadExpenses),
      switchMap(({ matterId, isBilled, skip, limit }) =>
        this.apiService.getExpenses({ matterId, isBilled, skip, limit }).pipe(
          map(response => BillingActions.loadExpensesSuccess({ response })),
          catchError(err =>
            of(BillingActions.loadExpensesFailure({ error: err?.error?.message ?? 'Failed to load expenses' }))
          )
        )
      )
    )
  );

  createExpense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.createExpense),
      switchMap(({ payload }) =>
        this.apiService.createExpense(payload).pipe(
          map(expense => BillingActions.createExpenseSuccess({ expense })),
          catchError(err =>
            of(BillingActions.createExpenseFailure({ error: err?.error?.message ?? 'Failed to create expense' }))
          )
        )
      )
    )
  );

  updateExpense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.updateExpense),
      switchMap(({ id, payload }) =>
        this.apiService.updateExpense(id, payload).pipe(
          map(expense => BillingActions.updateExpenseSuccess({ expense })),
          catchError(err =>
            of(BillingActions.updateExpenseFailure({ error: err?.error?.message ?? 'Failed to update expense' }))
          )
        )
      )
    )
  );

  deleteExpense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.deleteExpense),
      switchMap(({ id }) =>
        this.apiService.deleteExpense(id).pipe(
          map(() => BillingActions.deleteExpenseSuccess({ id })),
          catchError(err =>
            of(BillingActions.deleteExpenseFailure({ error: err?.error?.message ?? 'Failed to delete expense' }))
          )
        )
      )
    )
  );

  loadInvoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadInvoices),
      switchMap(({ matterId, status, skip, limit }) =>
        this.apiService.getInvoices({ matterId, status, skip, limit }).pipe(
          map(response => BillingActions.loadInvoicesSuccess({ response })),
          catchError(err =>
            of(BillingActions.loadInvoicesFailure({ error: err?.error?.message ?? 'Failed to load invoices' }))
          )
        )
      )
    )
  );

  createInvoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.createInvoice),
      switchMap(({ payload }) =>
        this.apiService.createInvoice(payload).pipe(
          map(invoice => BillingActions.createInvoiceSuccess({ invoice })),
          catchError(err =>
            of(BillingActions.createInvoiceFailure({ error: err?.error?.message ?? 'Failed to create invoice' }))
          )
        )
      )
    )
  );

  updateInvoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.updateInvoice),
      switchMap(({ id, payload }) =>
        this.apiService.updateInvoice(id, payload).pipe(
          map(invoice => BillingActions.updateInvoiceSuccess({ invoice })),
          catchError(err =>
            of(BillingActions.updateInvoiceFailure({ error: err?.error?.message ?? 'Failed to update invoice' }))
          )
        )
      )
    )
  );

  loadMatterBillingSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadMatterBillingSummary),
      switchMap(({ matterId }) =>
        this.apiService.getMatterBillingSummary(matterId).pipe(
          map(summary => BillingActions.loadMatterBillingSummarySuccess({ summary })),
          catchError(err =>
            of(BillingActions.loadMatterBillingSummaryFailure({ error: err?.error?.message ?? 'Failed to load billing summary' }))
          )
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly apiService: ApiService,
  ) {}
}
